import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Edit,
  Star,
  Layers,
  User,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Type,
  Image as ImageIcon,
  PlaySquare,
  Video,
  ExternalLink,
  GraduationCap,
  Map as MapIcon,
  Lock,
  BookOpen,
  Users,
  Share2,
} from 'lucide-react'
import { useSelector } from 'react-redux'
import { RootState } from '../../support/store'
import Carousel from './Carousel'
import ManageSeriesModal from './ManageSeriesModal'
import ModuleViewer from './ModuleViewer'
import { TextContentView } from './TextContentView'
import { CATEGORIES, Module, ModuleType } from './types'
import {
  useCompleteSeries,
  useInvalidateSeries,
  useMarkModuleViewed,
  useModuleImageUrl,
  useRateSeries,
  useSeriesDetail,
} from './hooks'
import { categoryAccent, userRatingValue, youtubeIdFromContent } from './utils'

const TYPE_STYLES: Record<ModuleType, { bg: string; text: string; label: string; icon: React.ComponentType<{ className?: string }> }> = {
  text: { bg: 'bg-[#fffbeb]', text: 'text-[#d97706]', label: 'Reading', icon: Type },
  image: { bg: 'bg-[#eff6ff]', text: 'text-[#2563eb]', label: 'Image', icon: ImageIcon },
  youtube: { bg: 'bg-[#fef2f2]', text: 'text-[#dc2626]', label: 'Video', icon: PlaySquare },
  vidcast: { bg: 'bg-[#faf5ff]', text: 'text-[#9333ea]', label: 'Video', icon: Video },
  mindtickle: { bg: 'bg-[#fff7ed]', text: 'text-[#ea580c]', label: 'Mindtickle', icon: ExternalLink },
  moodle: { bg: 'bg-[#f0fdfa]', text: 'text-[#0d9488]', label: 'Course', icon: GraduationCap },
  journey_map: { bg: 'bg-[#ecfdf5]', text: 'text-[#059669]', label: 'Journey Map', icon: MapIcon },
}

function ModuleThumb({ mod, seriesId }: { mod: Module; seriesId?: number | string }) {
  const [failed, setFailed] = useState(false)
  const style = TYPE_STYLES[mod.type] || TYPE_STYLES.text
  const TypeIcon = style.icon
  const youtubeId = mod.type === 'youtube' ? youtubeIdFromContent(mod.content) : null
  const image = useModuleImageUrl(mod.type === 'image' ? seriesId : undefined, mod)
  const imageUrl = mod.type === 'image' ? image.url : ''
  const hasImage = (mod.type === 'image' && !!imageUrl) || (mod.type === 'youtube' && !!youtubeId)

  return (
    <div className={`absolute inset-0 flex items-center justify-center ${style.bg}`}>
      {mod.type === 'image' && imageUrl && !failed && (
        <img src={imageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" onError={() => setFailed(true)} />
      )}
      {mod.type === 'youtube' && youtubeId && !failed && (
        <>
          <img
            src={`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setFailed(true)}
          />
          <div className="absolute inset-0 bg-black/25 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
              <PlaySquare className="w-6 h-6 text-[#dc2626]" />
            </div>
          </div>
        </>
      )}
      {(!hasImage || failed) && <TypeIcon className={`w-9 h-9 ${style.text}`} />}
    </div>
  )
}

// Only the active slide mounts ModuleViewer, so off-screen iframes do not autoplay.
function PreviewSlide({
  mod,
  seriesId,
  idx,
  total,
  active,
  onViewed,
  onPrev,
  onNext,
}: {
  mod: Module
  seriesId: number
  idx: number
  total: number
  active: boolean
  onViewed: () => void
  onPrev?: () => void
  onNext?: () => void
}) {
  return (
    <div className="h-full bg-white rounded-xl border border-[#e5e7eb] shadow-sm overflow-hidden">
      <div className="relative">
        {active ? (
          <ModuleViewer module={mod} seriesId={seriesId} onViewed={onViewed} />
        ) : (
          <div className="aspect-video relative">
            <ModuleThumb mod={mod} seriesId={seriesId} />
          </div>
        )}
        {onPrev && (
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              onPrev()
            }}
            aria-label="Previous episode"
            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/45 hover:bg-black/60 text-white flex items-center justify-center transition"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        {onNext && (
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              onNext()
            }}
            aria-label="Next episode"
            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/45 hover:bg-black/60 text-white flex items-center justify-center transition"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
      <div className="p-6">
        <p className="text-xs font-semibold uppercase tracking-wide mb-1.5 text-[#049fd9]">
          Episode {idx + 1} of {total}
        </p>
        <h2 className="text-xl font-semibold text-[#111827] mb-2">{mod.title}</h2>
        {mod.description && (
          <div className="mb-4">
            <TextContentView text={mod.description} format={mod.content?.descriptionFormat} className="max-w-2xl text-[#4b5563] text-sm leading-relaxed [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_strong]:font-semibold [&_a]:text-[#2563eb] [&_a]:underline" />
          </div>
        )}
        {mod.required && (
          <div className="inline-flex items-center gap-1 px-2 py-1 bg-[#fef2f2] text-[#dc2626] text-xs font-medium rounded">
            <Lock className="w-3 h-3" /> Required
          </div>
        )}
      </div>
    </div>
  )
}

export default function CyberBytesSeriesPage() {
  const params = useParams()
  const navigate = useNavigate()
  const seriesId = params.id as string
  const currentEmail = useSelector((state: RootState) => state.user.userData?.email) || null

  const detailQuery = useSeriesDetail(seriesId)
  const detail = detailQuery.data || null
  const loading = detailQuery.isLoading
  const error = detailQuery.isError ? detailQuery.error?.message || 'Failed to load series' : null
  const invalidateSeries = useInvalidateSeries()
  const refetchSeries = useCallback(() => {
    invalidateSeries(seriesId)
  }, [invalidateSeries, seriesId])

  const [currentModuleIndex, setCurrentModuleIndex] = useState(0)
  const [rating, setRating] = useState(0)
  const [reviewText, setReviewText] = useState('')
  const [rateError, setRateError] = useState<string | null>(null)
  const [rateSuccess, setRateSuccess] = useState(false)
  const [completeError, setCompleteError] = useState<string | null>(null)
  const [showManageModal, setShowManageModal] = useState(false)
  const [shareState, setShareState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const initializedIndexRef = useRef(false)

  const markViewed = useMarkModuleViewed(seriesId)
  const rateMutation = useRateSeries(seriesId)
  const completeMutation = useCompleteSeries(seriesId)

  useEffect(() => {
    initializedIndexRef.current = false
    setCurrentModuleIndex(0)
  }, [seriesId])

  useEffect(() => {
    if (!detail) return
    setRating(userRatingValue(detail.rating?.userRating))
    setReviewText('')
    // First load only: jump to the first unviewed episode, without yanking the player on refetch.
    if (!initializedIndexRef.current) {
      initializedIndexRef.current = true
      const mods = detail.modules || []
      const progress = detail.userProgress?.modules || []
      const firstUnviewedIdx = mods.findIndex((m) => !progress.find((p) => p.id === m.id)?.viewed)
      setCurrentModuleIndex(firstUnviewedIdx >= 0 ? firstUnviewedIdx : 0)
    }
  }, [detail])

  const series = detail?.series
  const modules = useMemo(() => detail?.modules || [], [detail])
  const currentModule = modules[currentModuleIndex]
  const overallProgress = detail?.userProgress?.overallProgress || 0
  const completedCount = detail?.userProgress?.modules?.filter((p) => p.viewed).length || 0
  const isOwner = !!(currentEmail && series && currentEmail === series.created_by?.email)
  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/cyber-bytes/${seriesId}` : ''

  const handleShare = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setShareState('copied')
    } catch {
      // Clipboard can be unavailable; fall back to the native share sheet.
      if (typeof navigator !== 'undefined' && navigator.share) {
        try {
          await navigator.share({ title: series?.title, url: shareUrl })
          return
        } catch {
          // cancelling the share sheet is not an error
        }
      }
      setShareState('failed')
    }
  }, [shareUrl, series?.title])

  useEffect(() => {
    if (shareState !== 'copied') return undefined
    const handle = setTimeout(() => setShareState('idle'), 2000)
    return () => clearTimeout(handle)
  }, [shareState])

  // Group by section, keeping each module's flat index for jump/active/viewed logic.
  const sections = useMemo(() => detail?.sections || [], [detail])
  const episodeGroups = useMemo(() => {
    const sorted = [...sections].sort((a, b) => a.order_index - b.order_index)
    const seen = new Set<number>()
    const groups: { section: (typeof sorted)[number] | null; items: { mod: Module; idx: number }[] }[] = []
    sorted.forEach((sec) => {
      const items = modules
        .map((mod, idx) => ({ mod, idx }))
        .filter(({ mod }) => mod.section === sec.id)
      groups.push({ section: sec, items })
      items.forEach(({ mod }) => seen.add(mod.id))
    })
    const ungrouped = modules.map((mod, idx) => ({ mod, idx })).filter(({ mod }) => !seen.has(mod.id))
    if (ungrouped.length > 0 || groups.length === 0) {
      groups.push({ section: null, items: ungrouped })
    }
    return groups
  }, [modules, sections])

  const handleModuleViewed = () => {
    if (!series || !currentModule) return
    const alreadyViewed = detail?.userProgress?.modules?.find((p) => p.id === currentModule.id)?.viewed
    if (alreadyViewed) return
    markViewed.mutate(currentModule.id, {
      onError: (err) => console.error('Failed to mark module as viewed:', err),
    })
  }

  const handleRateSeries = async () => {
    if (!series) return
    try {
      setRateError(null)
      setRateSuccess(false)
      await rateMutation.mutateAsync({ rating, review: reviewText || undefined })
      setRateSuccess(true)
    } catch (err: any) {
      console.error('Failed to rate series:', err)
      setRateError(err.message || 'Failed to submit rating')
    }
  }

  const handleCompleteSeries = async () => {
    if (!series) return
    try {
      setCompleteError(null)
      await completeMutation.mutateAsync()
    } catch (err: any) {
      console.error('Failed to complete series:', err)
      setCompleteError(err.message || 'Failed to complete series')
    }
  }

  if (loading) {
    return (
      <div className="cb-scope min-h-screen bg-[#f9fafb]">
        <div className="bg-white border-b border-[#e5e7eb]">
          <div className="max-w-7xl mx-auto px-6 pt-4 pb-5 animate-pulse">
            <div className="h-3 w-24 bg-[#f3f4f6] rounded mb-4" />
            <div className="flex items-start gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-[#f3f4f6] flex-shrink-0" />
              <div className="flex-1 min-w-0 space-y-2.5">
                <div className="h-3 w-28 bg-[#f3f4f6] rounded" />
                <div className="h-5 w-1/2 bg-[#e5e7eb] rounded" />
                <div className="h-3 w-2/3 bg-[#f3f4f6] rounded" />
              </div>
              <div className="w-40 h-8 bg-[#f3f4f6] rounded-lg flex-shrink-0" />
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-8 animate-pulse">
          <div className="grid gap-6 grid-cols-1 lg:grid-cols-3 items-start">
            <div className="lg:col-span-2">
              <div className="aspect-video bg-[#e5e7eb] rounded-xl" />
            </div>
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-5 space-y-2">
              <div className="h-4 w-20 bg-[#f3f4f6] rounded mb-2" />
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-12 bg-[#f3f4f6] rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !series) {
    return (
      <div className="cb-scope min-h-screen bg-[#f9fafb] flex items-center justify-center py-16">
        <div className="text-center">
          <h1 className="text-xl font-bold text-[#111827] mb-3">{error || 'Series not found'}</h1>
          <Link
            to="/cyber-bytes"
            className="inline-block px-5 py-2.5 bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg font-medium text-sm transition"
          >
            Back to Cyber Bytes
          </Link>
        </div>
      </div>
    )
  }

  const accent = categoryAccent(series.category)

  return (
    <div className="cb-scope min-h-screen bg-[#f9fafb]">
      <div className="bg-white border-b border-[#e5e7eb]">
        <div className="max-w-7xl mx-auto px-6 pt-4 pb-5">
          <Link to="/cyber-bytes" className="inline-flex items-center gap-1.5 text-[#6b7280] hover:text-[#374151] font-medium text-xs mb-4 transition">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Series
          </Link>

          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex items-start gap-3.5 min-w-[280px] flex-1">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${accent}14`, color: accent }}
              >
                <BookOpen className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: accent }}>
                    {CATEGORIES.find((c) => c.id === series.category)?.label || series.category || 'Series'}
                  </span>
                  <span
                    className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full border ${
                      series.published
                        ? 'bg-[#ecfdf5] text-[#047857] border-[#a7f3d0]'
                        : 'bg-[#fffbeb] text-[#b45309] border-[#fde68a]'
                    }`}
                  >
                    {series.published ? 'Published' : 'Draft'}
                  </span>
                </div>
                <h1 className="text-xl sm:text-2xl font-bold text-[#111827] leading-tight mb-1">{series.title}</h1>
                {series.description && <p className="text-[#6b7280] text-sm max-w-2xl line-clamp-2">{series.description}</p>}

                <div className="flex items-center gap-4 flex-wrap text-xs text-[#6b7280] mt-2.5">
                  <span className="flex items-center gap-1">
                    <Layers className="w-3.5 h-3.5" /> {modules.length} {modules.length === 1 ? 'episode' : 'episodes'}
                  </span>
                  {series.created_by?.display_name && (
                    <span className="flex items-center gap-1">
                      <User className="w-3.5 h-3.5" /> {series.created_by.display_name}
                    </span>
                  )}
                  {(series.viewers_count || 0) > 0 && (
                    <span className="flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" /> {series.viewers_count} viewers
                    </span>
                  )}
                  {(series.completed_count || 0) > 0 && (
                    <span className="flex items-center gap-1 text-[#059669]">
                      <CheckCircle2 className="w-3.5 h-3.5" /> {series.completed_count} completed
                    </span>
                  )}
                  {(detail?.rating?.count || 0) > 0 && (
                    <span className="flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 fill-[#facc15] text-[#facc15]" />
                      {(detail?.rating?.average || 0).toFixed(1)} ({detail?.rating?.count})
                    </span>
                  )}
                </div>

                {(series.tags?.length || 0) > 0 && (
                  <div className="flex gap-1.5 flex-wrap mt-2.5">
                    {series.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 bg-[#f9fafb] text-[#6b7280] text-[10px] font-medium rounded border border-[#f3f4f6]">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col items-end gap-2.5">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleShare}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#f3f4f6] hover:bg-[#e5e7eb] text-[#374151] rounded-lg font-medium text-xs transition"
                >
                  <Share2 className="w-3.5 h-3.5" /> {shareState === 'copied' ? 'Link copied' : 'Share'}
                </button>
                {isOwner && (
                  <button
                    onClick={() => setShowManageModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#f3f4f6] hover:bg-[#e5e7eb] text-[#374151] rounded-lg font-medium text-xs transition"
                  >
                    <Edit className="w-3.5 h-3.5" /> Manage
                  </button>
                )}
                {overallProgress === 100 && modules.length > 0 && (
                  <button
                    onClick={handleCompleteSeries}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg font-medium text-xs transition"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Mark Series Complete
                  </button>
                )}
              </div>
              <div className="w-40">
                <div className="flex items-center justify-between mb-1 text-[11px] text-[#6b7280]">
                  <span>Progress</span>
                  <span className="font-semibold text-[#374151]">{Math.round(overallProgress)}%</span>
                </div>
                <div className="w-full h-1.5 bg-[#f3f4f6] rounded-full overflow-hidden">
                  <div className="h-full bg-[#049fd9] rounded-full transition-all" style={{ width: `${overallProgress}%` }} />
                </div>
                {completeError && <p className="text-[#dc2626] text-[11px] mt-1 text-right">{completeError}</p>}
                {shareState === 'failed' && (
                  <p className="text-[#dc2626] text-[11px] mt-1 text-right break-all">Couldn&apos;t copy the link: {shareUrl}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid gap-6 grid-cols-1 lg:grid-cols-3 items-start">
          <div className="lg:col-span-2">
            {modules.length > 0 ? (
              <Carousel
                theme="light"
                ariaLabel="Episode player"
                itemClassName="w-[94%] sm:w-[90%]"
                gap="gap-6"
                accentColor="#049fd9"
                hoverGlow={false}
                showArrows={false}
                showEdgeFade={false}
                showDots={false}
                showProgressBar={false}
                snapAlign="center"
                hintLabel="Slide or use the arrows to move between episodes"
                activeIndex={currentModuleIndex}
                onSlideChange={setCurrentModuleIndex}
              >
                {modules.map((mod, idx) => (
                  <PreviewSlide
                    key={mod.id}
                    mod={mod}
                    seriesId={series.id}
                    idx={idx}
                    total={modules.length}
                    active={currentModuleIndex === idx}
                    onViewed={handleModuleViewed}
                    onPrev={currentModuleIndex === idx && idx > 0 ? () => setCurrentModuleIndex(idx - 1) : undefined}
                    onNext={currentModuleIndex === idx && idx < modules.length - 1 ? () => setCurrentModuleIndex(idx + 1) : undefined}
                  />
                ))}
              </Carousel>
            ) : (
              <div className="bg-white rounded-xl border border-[#e5e7eb] shadow-sm p-12 text-center">
                <div className="w-14 h-14 rounded-full bg-[#f3f4f6] flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="w-6 h-6 text-[#9ca3af]" />
                </div>
                <p className="text-[#374151] text-sm font-medium mb-1">No episodes yet</p>
                <p className="text-[#9ca3af] text-xs mb-4">
                  {isOwner ? 'Add your first module to get this series started.' : 'Check back once the creator adds content.'}
                </p>
                {isOwner && (
                  <button
                    onClick={() => setShowManageModal(true)}
                    className="px-4 py-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg font-medium text-sm transition"
                  >
                    Add Modules
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="lg:sticky lg:top-24">
            <div className="bg-white rounded-xl border border-[#e5e7eb] shadow-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-[#111827] text-sm">Episodes</h3>
                {modules.length > 0 && (
                  <span className="text-[11px] text-[#9ca3af] font-medium">{completedCount}/{modules.length} viewed</span>
                )}
              </div>
              <div className="space-y-1 max-h-[480px] overflow-y-auto -mx-1 px-1">
                {episodeGroups.map((group) => (
                  <div key={group.section?.id || 'ungrouped'} className="mb-2">
                    {sections.length > 0 && (
                      <div className="flex items-center gap-1.5 px-2.5 py-1.5 mt-1 first:mt-0">
                        <Layers className="w-3 h-3 text-[#9ca3af]" />
                        <span className="text-[11px] font-semibold text-[#6b7280] uppercase tracking-wide">
                          {group.section?.title || 'Other Episodes'}
                        </span>
                      </div>
                    )}
                    {group.items.map(({ mod, idx }) => {
                      const viewed = !!detail?.userProgress?.modules?.find((p) => p.id === mod.id)?.viewed
                      const style = TYPE_STYLES[mod.type] || TYPE_STYLES.text
                      const TypeIcon = style.icon
                      const active = currentModuleIndex === idx
                      return (
                        <button
                          key={mod.id}
                          onClick={() => setCurrentModuleIndex(idx)}
                          className={`w-full text-left rounded-lg p-2.5 transition flex items-center gap-3 outline-none focus-visible:ring-2 focus-visible:ring-[#049fd9]/50 ${
                            active ? 'bg-[#eff6ff]' : 'hover:bg-[#f9fafb]'
                          }`}
                        >
                          <div className="relative w-9 h-9 rounded-lg overflow-hidden flex-shrink-0">
                            <ModuleThumb mod={mod} seriesId={series.id} />
                          </div>
                          {viewed ? (
                            <CheckCircle2 className="w-5 h-5 text-[#22c55e] flex-shrink-0" />
                          ) : (
                            <div
                              className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                                active ? 'bg-[#049fd9] text-white' : 'bg-[#f3f4f6] text-[#6b7280]'
                              }`}
                            >
                              {idx + 1}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className={`text-xs font-medium line-clamp-2 ${active ? 'text-[#111827]' : 'text-[#374151]'}`}>{mod.title}</p>
                            <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-[#9ca3af]">
                              <TypeIcon className="w-3 h-3" /> {style.label}
                              {mod.required && (
                                <span className="flex items-center gap-0.5 text-[#ef4444] font-medium">
                                  <Lock className="w-2.5 h-2.5" /> Required
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                ))}
                {modules.length === 0 && <p className="text-xs text-[#9ca3af] text-center py-4">No episodes yet.</p>}
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 py-3.5 bg-white rounded-xl border border-[#e5e7eb] flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2.5">
            <span className="text-xs text-[#9ca3af] font-medium whitespace-nowrap">Rate this series</span>
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((r) => (
                <button
                  key={r}
                  onClick={() => {
                    setRating(r)
                    setRateSuccess(false)
                  }}
                  className="transition hover:scale-110"
                >
                  <Star className={`w-3.5 h-3.5 ${rating >= r ? 'fill-[#facc15] text-[#facc15]' : 'text-[#d1d5db]'}`} />
                </button>
              ))}
            </div>
            {(detail?.rating?.count || 0) > 0 && (
              <span className="text-[11px] text-[#9ca3af] whitespace-nowrap">
                {(detail?.rating?.average || 0).toFixed(1)} avg · {detail?.rating?.count} ratings
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-1 min-w-[220px] max-w-sm">
            <input
              value={reviewText}
              onChange={(e) => {
                setReviewText(e.target.value)
                setRateSuccess(false)
              }}
              placeholder="Optional review..."
              className="flex-1 px-3 py-1.5 border border-[#e5e7eb] rounded-lg text-xs focus:outline-none focus:border-[#049fd9]"
            />
            {rateSuccess ? (
              <span className="flex items-center gap-1 text-[#059669] text-xs font-medium whitespace-nowrap px-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Thanks!
              </span>
            ) : (
              <button
                onClick={handleRateSeries}
                disabled={rating === 0}
                className="px-3 py-1.5 bg-[#f3f4f6] hover:bg-[#e5e7eb] disabled:opacity-50 text-[#374151] rounded-lg font-medium text-xs transition whitespace-nowrap"
              >
                Submit
              </button>
            )}
          </div>
          {rateError && <p className="text-[#dc2626] text-xs w-full text-right">{rateError}</p>}
        </div>
      </div>

      {showManageModal && (
        <ManageSeriesModal
          series={series}
          modules={modules}
          sections={sections}
          onClose={() => setShowManageModal(false)}
          onSaved={refetchSeries}
          onDeleted={() => navigate('/cyber-bytes')}
        />
      )}
    </div>
  )
}
